import { PanelLeft } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './sheet';

import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';

const SIDEBAR_WIDTH = '16rem';
const SIDEBAR_WIDTH_MOBILE = '18rem';
const SIDEBAR_WIDTH_ICON = '3.5rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

interface SidebarContextValue {
  state: 'expanded' | 'collapsed';
  isMobile: boolean;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  toggleSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used within a SidebarProvider');
  return ctx;
}

/** Trimmed-down port of shadcn's sidebar primitive — collapsible-to-icon on
 * desktop (`lg:`), a `Sheet` drawer on mobile. Scoped to what `AppShell`
 * needs (DEC-0043): no offcanvas/floating/inset variants, no resize rail,
 * no `asChild` polymorphism — nav content is authored directly by the
 * caller rather than through `SidebarMenu*` subcomponents. */
export function SidebarProvider({ children }: { children: ReactNode }): JSX.Element {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = useState(false);
  const [open, setOpen] = useState(true);

  const toggleSidebar = useCallback(() => {
    if (isMobile) setOpenMobile((v) => !v);
    else setOpen((v) => !v);
  }, [isMobile]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleSidebar]);

  const value = useMemo<SidebarContextValue>(
    () => ({
      state: open ? 'expanded' : 'collapsed',
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [open, isMobile, openMobile, toggleSidebar],
  );

  return (
    <SidebarContext.Provider value={value}>
      <div className="lg:flex min-h-screen">{children}</div>
    </SidebarContext.Provider>
  );
}

export function Sidebar({
  header,
  footer,
  children,
}: {
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent
          side="left"
          className="w-(--sidebar-width) bg-sidebar text-sidebar-foreground p-0 [&>button]:hidden"
          style={{ '--sidebar-width': SIDEBAR_WIDTH_MOBILE } as CSSProperties}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation</SheetTitle>
            <SheetDescription>App navigation menu</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col">
            {header}
            <div className="flex-1 overflow-y-auto">{children}</div>
            {footer}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      data-state={state}
      className={cn(
        'group hidden lg:flex flex-col shrink-0 h-screen sticky top-0 overflow-hidden',
        'bg-sidebar text-sidebar-foreground border-r border-sidebar-border',
        'transition-[width] duration-200 ease-linear',
      )}
      style={{ width: state === 'expanded' ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_ICON }}
    >
      {header}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">{children}</div>
      {footer}
    </aside>
  );
}

export function SidebarInset({ children }: { children: ReactNode }): JSX.Element {
  return <div className="flex-1 min-w-0 flex flex-col">{children}</div>;
}

export function SidebarTrigger({ className }: { className?: string }): JSX.Element {
  const { toggleSidebar } = useSidebar();
  return (
    <button
      type="button"
      onClick={toggleSidebar}
      aria-label="Toggle sidebar"
      className={cn(
        'inline-flex items-center justify-center w-9 h-9 rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shrink-0',
        className,
      )}
    >
      <PanelLeft className="w-[18px] h-[18px]" />
    </button>
  );
}
